/* ==========================================================================
   Clapstore — UGC reels.

   Playback is a budget, not a default.

   The section ships every <video> with preload="none" and NO <source> child at
   all. This file is what decides, moment to moment, which two clips are worth
   spending a decoder and a network buffer on. Everything else stays a poster.

   Contract with sections/cs-reels.liquid and assets/cs-reels.css
   -------------------------------------------------------------
   container  [data-cs-reels]           data-cs-autoplay="true|false"
              .is-live                  autoplay is on; sound toggles are shown
              .is-manual                visitor drives; play buttons are shown
   card       [data-cs-reel]
              .is-video                 a frame has painted; fade the video in
              .is-playing               currently playing
              .is-blocked               the browser refused play(); offer a button
   video      video.cs-reel__video[data-cs-src]
   controls   [data-cs-play]  [data-cs-mute] + [data-cs-mute-label]

   Performance rules this file obeys
   ---------------------------------
   * ONE IntersectionObserver for the whole page, not one per section.
   * The observer is the only thing watching scroll position. There is no
     scroll handler here at all, so there is nothing to throttle and nothing
     that can force a synchronous layout mid-flick.
   * Ratios come from the observer's own entries. We never call
     getBoundingClientRect during scrolling — that is the classic cause of a
     rail that feels gluey.
   * State is applied in one pass per callback, so class writes are batched
     rather than interleaved with reads.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR   = '[data-cs-reels]';
  var MAX_ACTIVE = 2;     // never more than two decoders live at once
  var MIN_RATIO  = 0.5;   // a clip is only a candidate at >= 50% on screen

  /* ---------------------------------------------------------------------
     Should this visitor get autoplay at all?

     Three separate reasons to say no, and all of them are the visitor's
     call rather than ours: they asked for less motion, they asked us to
     save their data, or they are on a connection where a video would just
     be a stalled spinner.
     --------------------------------------------------------------------- */
  function autoplayAllowed() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) {
      if (c.saveData) return false;
      if (/^(slow-)?2g$/.test(c.effectiveType || '')) return false;
    }
    return true;
  }

  /* ---------------------------------------------------------------------
     Attaching and releasing a clip.

     Adding the <source> late is what keeps the initial page cost at one
     poster per card. Removing it and calling load() again is what actually
     frees the buffer — pausing alone leaves the download and the decoder
     sitting there, which is the whole problem this section was built to
     avoid.
     --------------------------------------------------------------------- */
  function attach(video) {
    if (video.dataset.csAttached === '1') return;
    var url = video.dataset.csSrc;
    if (!url) return;

    var source = document.createElement('source');
    source.setAttribute('src', url);
    source.setAttribute('type', 'video/mp4');
    video.appendChild(source);
    video.dataset.csAttached = '1';
    video.load();
  }

  function release(card, video) {
    if (video.dataset.csAttached !== '1') return;
    try { video.pause(); } catch (e) {}

    while (video.firstChild) video.removeChild(video.firstChild);
    video.removeAttribute('src');
    delete video.dataset.csAttached;
    try { video.load(); } catch (e) {}   // aborts the fetch, drops the buffer

    card.classList.remove('is-playing', 'is-video', 'is-blocked');
  }

  /* ---------------------------------------------------------------------
     One reel card.
     --------------------------------------------------------------------- */
  function Reel(card, group) {
    this.card  = card;
    this.group = group;
    this.video = card.querySelector('.cs-reel__video');
    this.ratio = 0;
    this.wanted = false;

    if (!this.video) return;

    var self = this;

    // 'playing' is the first frame that has actually painted. Fading on
    // anything earlier shows a black box for a beat.
    this.video.addEventListener('playing', function () {
      self.card.classList.add('is-video', 'is-playing');
      self.card.classList.remove('is-blocked');
    });

    this.video.addEventListener('pause', function () {
      self.card.classList.remove('is-playing');
    });

    // Manual play button — always a real user gesture, so this is also the
    // moment a browser that refused autoplay will relent.
    var play = card.querySelector('[data-cs-play]');
    if (play) {
      play.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();          // never follow the card's stretched link
        self.wanted = true;
        group.request(self, true);
      });
    }

    // Tap the frame -> fullscreen player.
    //
    // The card keeps doing what it always did: autoplay muted in the rail. What
    // changes is that the frame is now also a door. The player itself lives in
    // cs-reel-player.js/.css, which are NOT on the page yet — openPlayer fetches
    // them on this first tap. A visitor who never opens a reel pays nothing for
    // the feature, which is the only way it was worth adding.
    //
    // The mute and play buttons stopPropagation above, so they still work
    // inline without opening anything.
    // The listener goes on the CARD, not the frame, and the frame's geometry
    // decides. That is not the obvious shape, and it is not arbitrary:
    // .cs-reel__link::after is stretched over the whole card at inset 0, and a
    // pseudo-element dispatches its clicks to the <a> that owns it. That <a>
    // lives in the card foot, outside .cs-reel__frame — so a listener on the
    // frame never fires for a tap on the video, because the event target is not
    // inside it. Hit-testing against the frame's rect is what actually
    // separates "tapped the clip" from "tapped the product name".
    var frame = card.querySelector('.cs-reel__frame');
    if (frame && card.querySelector('[data-cs-reel-item]')) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('[data-cs-mute], [data-cs-play], form, button[type="submit"]')) return;
        var r = frame.getBoundingClientRect();
        if (e.clientY < r.top || e.clientY > r.bottom) return;   // the foot still follows the link
        e.preventDefault();
        openPlayer(group.root, card);
      });
    }

    // Sound toggle. aria-pressed="true" means muted, matching the CSS.
    var mute = card.querySelector('[data-cs-mute]');
    if (mute) {
      mute.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var nowMuted = mute.getAttribute('aria-pressed') !== 'false';
        if (nowMuted) {
          group.soloAudio(self);      // only ever one clip audible
          self.setMuted(false);
        } else {
          self.setMuted(true);
        }
      });
      this.muteBtn = mute;
      this.muteLabel = card.querySelector('[data-cs-mute-label]');
    }
  }

  Reel.prototype.setMuted = function (muted) {
    if (!this.video) return;
    this.video.muted = muted;

    var btn = this.muteBtn;
    if (!btn) return;
    btn.setAttribute('aria-pressed', muted ? 'true' : 'false');

    var label = muted ? btn.dataset.csLabelMuted : btn.dataset.csLabelUnmuted;
    if (label) {
      btn.setAttribute('aria-label', label);
      if (this.muteLabel) this.muteLabel.textContent = label;
    }
  };

  Reel.prototype.start = function () {
    if (!this.video) return;
    attach(this.video);

    var self = this;
    var p = this.video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        // Autoplay refused. Surface the play button instead of failing silently.
        self.card.classList.add('is-blocked');
        self.card.classList.remove('is-playing');
      });
    }
  };

  Reel.prototype.stop = function (fully) {
    if (!this.video) return;
    if (fully) {
      release(this.card, this.video);
      this.wanted = false;
    } else {
      try { this.video.pause(); } catch (e) {}
      this.card.classList.remove('is-playing');
    }
  };

  /* ---------------------------------------------------------------------
     One section instance.
     --------------------------------------------------------------------- */
  function Group(root, live) {
    this.root = root;
    this.live = live;
    this.reels = [];

    root.classList.add(live ? 'is-live' : 'is-manual');

    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-cs-reel]'), function (card) {
      var reel = new Reel(card, self);
      if (reel.video) {
        card._csReel = reel;
        self.reels.push(reel);
      }
    });
  }

  // Only one clip may be audible at a time, across every section on the page.
  Group.prototype.soloAudio = function (except) {
    this.reels.forEach(function (r) {
      if (r !== except) r.setMuted(true);
    });
  };

  // A manual play request wins a slot immediately.
  Group.prototype.request = function (reel, force) {
    if (force) {
      var actives = this.reels.filter(function (r) {
        return r !== reel && r.card.classList.contains('is-playing');
      });
      while (actives.length >= MAX_ACTIVE) actives.pop().stop(false);
    }
    reel.start();
  };

  /* Decide who plays, from ratios the observer already handed us.
     Sorting a short array beats keeping a hand-rolled "most visible" cache
     and getting it subtly wrong. */
  Group.prototype.settle = function () {
    if (!this.live) return;

    var candidates = this.reels
      .filter(function (r) { return r.ratio >= MIN_RATIO; })
      .sort(function (a, b) { return b.ratio - a.ratio; });

    var winners = candidates.slice(0, MAX_ACTIVE);

    this.reels.forEach(function (r) {
      var shouldPlay = winners.indexOf(r) !== -1;

      if (shouldPlay) {
        if (!r.card.classList.contains('is-playing')) r.start();
      } else if (r.ratio <= 0) {
        r.stop(true);           // fully offscreen: release the buffer
      } else if (r.card.classList.contains('is-playing')) {
        r.stop(false);          // partly visible: just pause, keep the buffer
      }
    });
  };

  Group.prototype.pauseAll = function () {
    this.reels.forEach(function (r) { r.stop(false); });
  };

  /* ---------------------------------------------------------------------
     Boot.
     --------------------------------------------------------------------- */
  var groups = [];
  var observer = null;

  function ensureObserver() {
    if (observer || !('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver(function (entries) {
      var touched = [];
      entries.forEach(function (entry) {
        var reel = entry.target._csReel;
        if (!reel) return;
        reel.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        if (touched.indexOf(reel.group) === -1) touched.push(reel.group);
      });
      touched.forEach(function (g) { g.settle(); });
    }, {
      // Several thresholds so a slow scroll updates ratios smoothly rather
      // than flipping hard at exactly 50%.
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    if (!roots.length) return;                    // no reels: do nothing at all

    var allowed = autoplayAllowed();

    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.csReelsReady === '1') return;
      root.dataset.csReelsReady = '1';

      var live = allowed && root.dataset.csAutoplay !== 'false';
      var group = new Group(root, live);
      groups.push(group);

      if (!live) return;                          // manual mode needs no observer

      ensureObserver();
      if (!observer) {                            // no IO support: stay manual
        root.classList.remove('is-live');
        root.classList.add('is-manual');
        group.live = false;
        return;
      }
      group.reels.forEach(function (r) { observer.observe(r.card); });
    });
  }

  // A backgrounded tab should not be decoding video.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) groups.forEach(function (g) { g.pauseAll(); });
    else groups.forEach(function (g) { g.settle(); });
  });

  /* ---------------------------------------------------------------- player */

  // Loaded once, on demand. Two <link>/<script> injections, then the module
  // stays resident for the rest of the session.
  var playerReady = null;
  function loadPlayer(root) {
    if (playerReady) return playerReady;
    var css = root.getAttribute('data-cs-player-css');
    var js = root.getAttribute('data-cs-player-js');
    playerReady = new Promise(function (resolve, reject) {
      if (css && !document.querySelector('link[data-cs-reel-player]')) {
        var l = document.createElement('link');
        l.rel = 'stylesheet'; l.href = css; l.setAttribute('data-cs-reel-player', '');
        document.head.appendChild(l);
      }
      if (window.CsReelPlayer) return resolve(window.CsReelPlayer);
      var t = document.createElement('script');
      t.src = js;
      t.onload = function () {
        window.CsReelPlayer ? resolve(window.CsReelPlayer) : reject(new Error('player did not register'));
      };
      t.onerror = function () { reject(new Error('player failed to load')); };
      document.head.appendChild(t);
    });
    return playerReady;
  }

  function openPlayer(root, card) {
    var nodes = [].slice.call(root.querySelectorAll('[data-cs-reel-item]'));
    var index = nodes.indexOf(card.querySelector('[data-cs-reel-item]'));
    var items = [];
    for (var i = 0; i < nodes.length; i++) {
      try { items.push(JSON.parse(nodes[i].textContent)); } catch (err) { /* skip a malformed card, keep the rest */ }
    }
    if (!items.length) return;
    loadPlayer(root).then(function (P) {
      P.open(items, index < 0 ? 0 : index, {
        currency: root.getAttribute('data-cs-currency') || 'INR',
        onAddToCart: function (product) {
          if (!product || !product.variantId) throw new Error('This item is unavailable');
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] })
          }).then(function (r) {
            if (!r.ok) throw new Error('Could not add to cart');
            window.location.href = '/cart';
          });
        }
      });
    })['catch'](function (err) {
      // If the player will not load, the card is still a link to the product.
      console.warn('[cs-reels] player unavailable', err);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    groups = groups.filter(function (g) {
      if (!e.target.contains(g.root)) return true;
      g.reels.forEach(function (r) {
        if (observer) observer.unobserve(r.card);
        r.stop(true);
      });
      return false;
    });
  });
})();
